import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/container";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import FAQSection from "@/components/ui/faq-section";
import {
  CheckCircle,
  Users,
  Award,
  Clock,
  Shield,
  TrendingUp,
  ArrowRight,
  Zap,
  Settings,
  Target,
  Star,
  Phone,
  Mail,
  MessageCircle
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";
import { AuroraHero } from "@/components/ui/aurora-hero";

const ConsultoriaFluig = () => {
  const benefits = [
    {
      icon: Award,
      title: "Especialistas Certificados",
      description: "Equipe 100% certificada TOTVS Fluig com mais de 10 anos de experiência"
    },
    {
      icon: Clock,
      title: "Implementação Rápida",
      description: "Metodologia ágil com entregas em até 30 dias para projetos padrão"
    },
    {
      icon: Shield,
      title: "ROI Garantido",
      description: "Retorno sobre investimento médio de 300-500% no primeiro ano"
    },
    {
      icon: TrendingUp,
      title: "Suporte Contínuo",
      description: "Acompanhamento pós-implementação e suporte especializado 24/7"
    }
  ];

  const services = [
    {
      title: "Análise e Diagnóstico",
      description: "Avaliação completa dos processos atuais e identificação de oportunidades de automação",
      features: ["Mapeamento de processos", "Análise de viabilidade", "Proposta técnica detalhada"]
    },
    {
      title: "Implementação Fluig",
      description: "Desenvolvimento e configuração da plataforma Fluig de acordo com suas necessidades",
      features: ["Configuração da plataforma", "Desenvolvimento de workflows", "Integrações necessárias"]
    },
    {
      title: "Treinamento e Capacitação",
      description: "Capacitação completa da equipe para utilização eficiente da plataforma",
      features: ["Treinamento de usuários", "Documentação técnica", "Manuais de operação"]
    },
    {
      title: "Suporte e Evolução",
      description: "Acompanhamento contínuo e evolução da plataforma conforme necessidades",
      features: ["Suporte técnico", "Novas funcionalidades", "Otimizações de performance"]
    }
  ];

  const testimonials = [
    {
      company: "Indústria Química",
      feedback: "A consultoria Future Station transformou nossos processos. Reduzimos 70% do tempo em aprovações e eliminamos 100% do papel.",
      result: "ROI de 450% em 18 meses"
    },
    {
      company: "Rede de Franquias",
      feedback: "Implementação perfeita em todas as 200 lojas. A padronização dos processos trouxe visibilidade total da operação.",
      result: "30% aumento produtividade"
    },
    {
      company: "Hospital Regional",
      feedback: "O Fluig revolucionou nossa gestão hospitalar. Prontuário eletrônico integrado e processos 100% digitais.",
      result: "60% redução tempo atendimento"
    }
  ];

  const stats = [
    { number: "500+", label: "Projetos Entregues" },
    { number: "10+", label: "Anos de Experiência" },
    { number: "99.9%", label: "Taxa de Sucesso" },
    { number: "24/7", label: "Suporte Disponível" }
  ];

  return (
    <div className="min-h-screen bg-background dark:bg-black text-foreground dark:text-white">
      <SEO 
        title="Consultoria Fluig Especializada - Future Station"
        description="Consultoria Fluig especializada com mais de 500 projetos entregues. ROI garantido, implementação em 30 dias e suporte 24/7. Diagnóstico gratuito disponível."
        keywords={['consultoria fluig', 'especialista fluig', 'consultoria totvs fluig', 'implementação fluig', 'automação processos fluig', 'consultoria bpm', 'fluig consultant']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-5xl mx-auto">
              <Badge variant="outline" className="mb-6 text-fs-cyan-400 border-fs-cyan-400/30">
                Consultoria Especializada
              </Badge>

              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                Consultoria
                <span className="block fs-gradient-text mt-2">
                  Fluig Especializada
                </span>
              </h1>

              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto mb-8 leading-relaxed">
                Transforme seus processos empresariais com a consultoria Fluig mais experiente do Brasil.
                Mais de 500 projetos entregues, ROI garantido e metodologia comprovada.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button
                  size="lg"
                  variant="fs-primary"
                  onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Quero saber mais sobre consultoria Fluig.', '_blank')}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Diagnóstico Gratuito
                </Button>
                <Button
                  size="lg"
                  variant="fs-secondary"
                  onClick={() => document.getElementById('servicos')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Nossos Serviços
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold fs-gradient-text mb-2">
                      {stat.number}
                    </div>
                    <div className="text-muted-foreground dark:text-white/70 text-sm">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <Container>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Por que Escolher a Future Station?
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                  Diferenciais que fazem da Future Station a melhor escolha para consultoria Fluig
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="bg-card dark:bg-gray-900/50 border border-border hover:border-fs-purple-500/40 transition-all duration-300 text-center">
                    <CardContent className="p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mb-4">
                        <benefit.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground dark:text-white mb-3">
                        {benefit.title}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 text-sm">
                        {benefit.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Services Section */}
        <section id="servicos" className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-magenta-500/10 rounded-full blur-3xl" />
          </div>

          <Container className="relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Nossos Serviços de Consultoria
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                  Metodologia comprovada para implementação de sucesso
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {services.map((service, index) => (
                  <Card key={index} className="bg-card dark:bg-gray-900/50 border border-border hover:border-fs-purple-500/40 transition-all duration-300">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-foreground dark:text-white mb-4">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 mb-6">
                        {service.description}
                      </p>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center text-muted-foreground dark:text-white/70">
                            <CheckCircle className="w-4 h-4 text-fs-cyan-500 mr-3 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Testimonials */}
        <section className="py-20">
          <Container>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Cases de Sucesso
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                  Resultados reais de clientes que transformaram seus negócios
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                  <Card key={index} className="bg-gradient-to-br from-fs-purple-500/10 to-fs-cyan-500/10 border-fs-purple-500/20">
                    <CardContent className="p-6">
                      <div className="flex items-center mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <p className="text-muted-foreground dark:text-white/70 mb-4 italic">
                        "{testimonial.feedback}"
                      </p>
                      <div className="text-fs-cyan-400 font-semibold text-sm mb-2">
                        {testimonial.result}
                      </div>
                      <div className="text-muted-foreground/70 dark:text-white/50 text-sm">
                        {testimonial.company}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-fs-purple-500/10 via-fs-magenta-500/10 to-fs-cyan-500/10">
          <Container>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Pronto para Transformar seus Processos?
              </h2>
              <p className="text-lg text-muted-foreground dark:text-white/70 mb-8 max-w-2xl mx-auto">
                Agende hoje mesmo um diagnóstico gratuito e descubra como nossa consultoria
                Fluig pode revolucionar sua empresa.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  size="lg"
                  variant="fs-primary"
                  onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Quero agendar um diagnóstico gratuito.', '_blank')}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Agora
                </Button>
                <Button
                  size="lg"
                  variant="fs-secondary"
                  onClick={() => window.location.href = '/contato'}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Solicitar Proposta
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-muted-foreground/80 dark:text-white/60">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  (81) 98165-8439
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  contato@futurestation.com.br
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <Container>
            <FAQSection 
              title="Perguntas Frequentes - Consultoria Fluig"
              description="Esclareça suas dúvidas sobre nossa consultoria especializada em Fluig"
              faqs={[
                {
                  question: "Quanto tempo demora para implementar o Fluig?",
                  answer: "O prazo varia conforme a complexidade e escopo do projeto. Projetos simples podem ser implementados em 15-30 dias, enquanto projetos complexos com múltiplas integrações podem levar 2-3 meses. Utilizamos metodologia ágil com entregas incrementais."
                },
                {
                  question: "Qual o investimento para uma consultoria Fluig?",
                  answer: "O investimento varia conforme o escopo do projeto. Oferecemos diagnóstico gratuito para avaliar suas necessidades. Os valores incluem licenciamento, implementação, treinamento e suporte. Garantimos ROI médio de 300-500% no primeiro ano."
                },
                {
                  question: "Vocês oferecem suporte após a implementação?",
                  answer: "Sim, oferecemos suporte completo pós-implementação incluindo: suporte técnico 24/7, manutenção preventiva, atualizações da plataforma, desenvolvimento de novas funcionalidades e treinamentos adicionais conforme necessário."
                },
                {
                  question: "Como funciona o diagnóstico gratuito?",
                  answer: "O diagnóstico gratuito inclui: análise dos processos atuais, identificação de oportunidades de automação, proposta técnica detalhada, cronograma de implementação e cálculo de ROI esperado. É realizado por especialistas certificados sem qualquer compromisso."
                },
                {
                  question: "Vocês trabalham com integrações com outros sistemas?",
                  answer: "Sim, somos especialistas em integrações. Conectamos o Fluig com ERPs (TOTVS, SAP, Oracle), sistemas de RH, CRMs, bancos de dados e APIs diversas. Garantimos integração total com seu ecossistema tecnológico atual."
                },
                {
                  question: "A equipe da Future Station é certificada?",
                  answer: "Sim, nossa equipe é 100% certificada TOTVS Fluig com mais de 10 anos de experiência. Possuímos as principais certificações: Fluig Developer, Fluig Administrator, Fluig Architect e TOTVS Partner. Participamos ativamente da comunidade Fluig."
                }
              ]}
            />
          </Container>
        </section>
        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Explore Nossas Outras Soluções"
              links={[
                {
                  title: "Serviços de TI Especializados",
                  description: "Outsourcing Fluig completo com equipe dedicada, desenvolvimento customizado e suporte 24/7.",
                  href: "/servicos/ti",
                  category: "Serviços"
                },
                {
                  title: "Automação de Processos",
                  description: "Transforme processos manuais com automação inteligente usando nossa expertise em Fluig.",
                  href: "/automacao-processos",
                  category: "Automação"
                },
                {
                  title: "Catálogo FluigApps",
                  description: "Acelere implementação com nossos 16 FluigApps prontos para diversos processos empresariais.",
                  href: "/flows",
                  category: "Soluções"
                },
                {
                  title: "Consultoria em IA",
                  description: "Combine Fluig com inteligência artificial para processos ainda mais inteligentes e eficientes.",
                  href: "/consultoria-ia",
                  category: "Consultoria"
                }
              ]}
            />
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ConsultoriaFluig;